import { ErrorHandler } from '@src/components/ErrorHandler';
import { useApi } from '@src/context/Api';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { isArray, isEmpty, isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useState } from 'react';

import { Styles } from '../includes/styles';

/**
 * ExpansionRow
 *
 * Displays the expanded row for the member with additional information.
 *
 * @param row
 * @returns {Element}
 * @constructor
 */
export function ExpansionRow({ row: memberData }) {
  const [additionalAddresses, setAdditionalAddresses] = useState([]);

  const { useApiQuery } = useApi();

  const [GetAddresses, { data: addressData, isFetching: isFetchingAddresses, isLoading: isLoadingAddresses }] =
    useApiQuery('GetAddresses');

  /**
   * Update the additional addresses and current version when the address data changes.
   */
  useEffect(() => {
    if (!isUndefined(addressData?.addresses) && !isEmpty(addressData?.addresses)) {
      setAdditionalAddresses(addressData?.addresses);
    }
  }, [addressData]);

  /**
   * Retrieve additional addresses for the member from the API.
   */
  useLayoutEffect(() => {
    if (!isUndefined(memberData) && !isEmpty(memberData)) {
      if (!isLoadingAddresses && !isFetchingAddresses && isUndefined(addressData)) {
        GetAddresses(memberData);
      }
    }
  }, [addressData, isFetchingAddresses, isLoadingAddresses, memberData]);

  return (
    <ErrorHandler location="src/routes/private/Search/components/Results/components/Table/components/ExpansionRow/ExpansionRow.jsx">
      <Styles>
        <motion.div
          animate={memberData ? 'open' : 'closed'}
          initial={{ opacity: 0 }}
          variants={{
            closed: { opacity: 0 },
            open: { opacity: 1 },
          }}
        >
          {isLoadingAddresses || isFetchingAddresses ? (
            <div style={{ padding: '20px', width: '100%' }}>
              <LoadingSpinner ariaLoadingLabel="Loading..." isLoading size="$lg" style={{ textAlign: 'center' }} />
            </div>
          ) : (
            <div style={{ margin: 'var(--abyss-space-lg) auto', width: '75%' }}>
              {isArray(additionalAddresses) &&
                !isEmpty(additionalAddresses) &&
                additionalAddresses?.map((val) => {
                  const { addresses, ccEffectiveDate, ccEndDate, confCommEntryId, confComType } = val;
                  const address = addresses.find((addr) => {
                    return !addr.isInternational;
                  });
                  return (
                    <Grid key={confCommEntryId}>
                      <Grid.Col span={{ xs: '25%' }}>
                        <p>
                          <strong>Type</strong>
                        </p>
                        <p>{confComType || <React.Fragment>&mdash;</React.Fragment>}</p>
                      </Grid.Col>
                      <Grid.Col span={{ xs: '25%' }}>
                        <p>
                          <strong>Address</strong>
                        </p>
                        <div>
                          {address?.line1 &&
                          address?.city &&
                          address?.stateOrRegion &&
                          address?.countryCode &&
                          address?.postalCode ? (
                            <address>
                              {address?.line1}, {address?.line2}, {address?.line3}
                              <br />
                              {address?.city}, {address?.stateOrRegion}
                              <br />
                              {address?.countryCode}
                              <br />
                              {address?.postalCode}
                            </address>
                          ) : (
                            <React.Fragment>&mdash;</React.Fragment>
                          )}
                        </div>
                      </Grid.Col>
                      <Grid.Col span={{ xs: '50%' }}>
                        <p>
                          <strong>Effective Date</strong>
                        </p>
                        <p>
                          {ccEffectiveDate && ccEndDate ? (
                            <React.Fragment>
                              {dayjs(ccEffectiveDate).format('MM/DD/YYYY')} to {dayjs(ccEndDate).format('MM/DD/YYYY')}
                            </React.Fragment>
                          ) : (
                            <React.Fragment>&mdash;</React.Fragment>
                          )}
                        </p>
                      </Grid.Col>
                    </Grid>
                  );
                })}
            </div>
          )}
        </motion.div>
      </Styles>
    </ErrorHandler>
  );
}

ExpansionRow.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      addressLine: PropTypes.string,
      cagId: PropTypes.arrayOf(
        PropTypes.shape({
          carrierId: PropTypes.string,
        })
      ),
      city: PropTypes.string,
      policyEndDate: PropTypes.string,
      policyStartDate: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
    }),
  }),
};

ExpansionRow.defaultProps = {
  row: {},
};
